<template>
  <v-container
    fluid
    class="email-campaigns mt-2"
    :class="
      $vuetify.breakpoint.mdAndUp
        ? 'py-5 px-10'
        : '' && $vuetify.breakpoint.smAndUp
        ? 'py-2 px-2'
        : ''
    "
  >
    <!-- Scroll to top button -->
    <v-btn
      v-show="showScrollTop"
      fab
      dark
      fixed
      bottom
      left
      color="primary"
      @click="scrollToTop"
      class="mb-5 mr-5"
    >
      <v-icon>mdi-arrow-up</v-icon>
    </v-btn>

    <PageHeader title="Email Campaigns" />

    <v-card class="mb-6">
      <v-card-title>
        Marketing Email Campaign
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search users"
          single-line
          hide-details
          clearable
        ></v-text-field>
      </v-card-title>

      <v-card-text>
        <!-- Error alert -->
        <v-alert v-if="error" type="error" class="mb-4" dismissible>
          {{ error }}
        </v-alert>

        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="selectedTemplate"
              :items="emailTemplates"
              label="Email Template"
              item-text="name"
              item-value="path"
              @change="previewEmailTemplate"
              return-object
              :disabled="loading"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.subject }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
            <div
              v-if="selectedTemplate"
              class="text-caption grey--text text--darken-1 mt-n2"
            >
              {{ selectedTemplate.subject }}
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="selectedFilter"
              :items="filterOptions"
              label="Filter Users"
              placeholder="Please select a filter"
              @change="applyFilter"
              :disabled="loading"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-btn
              :color="allSelectedUsersAlreadySent ? 'warning' : 'primary'"
              :loading="sending"
              :disabled="
                !selectedTemplate ||
                  selectedUsers.length === 0 ||
                  loading ||
                  allSelectedUsersAlreadySent
              "
              @click="sendCampaign"
              class="mr-2"
            >
              Send Campaign
            </v-btn>
            <v-tooltip
              bottom
              v-if="allSelectedUsersAlreadySent && selectedUsers.length > 0"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="warning" v-bind="attrs" v-on="on">
                  <v-icon>mdi-alert</v-icon>
                </v-btn>
              </template>
              <span
                >All selected users have already received an email today</span
              >
            </v-tooltip>
            <v-btn
              text
              class="ml-2"
              :disabled="!selectedTemplate || loading"
              @click="previewDialog = true"
            >
              Preview
            </v-btn>
            <v-btn
              icon
              class="ml-2"
              :loading="loading"
              @click="fetchUsers({ force: true })"
              title="Refresh Users"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Add new section for comma-separated emails -->
        <v-row>
          <v-col cols="12">
            <v-btn
              text
              @click="showExternalEmailSection = !showExternalEmailSection"
              class="mb-2"
            >
              <v-icon left>{{
                showExternalEmailSection ? 'mdi-chevron-up' : 'mdi-chevron-down'
              }}</v-icon>
              {{
                showExternalEmailSection
                  ? 'Hide External Email List'
                  : 'Show External Email List'
              }}
            </v-btn>
            <v-expand-transition>
              <v-card v-show="showExternalEmailSection" outlined class="pa-4">
                <v-card-title class="text-subtitle-1">
                  Send to External Email List
                </v-card-title>
                <v-card-text>
                  <v-textarea
                    v-model="externalEmails"
                    label="Enter email addresses (comma-separated)"
                    hint="Enter email addresses separated by commas"
                    persistent-hint
                    rows="3"
                    :disabled="loading || sending"
                  ></v-textarea>
                  <v-btn
                    color="primary"
                    :loading="sending"
                    :disabled="!selectedTemplate || !externalEmails || loading"
                    @click="sendToExternalEmails"
                  >
                    Send to External List
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-expand-transition>
          </v-col>
        </v-row>

        <v-alert
          v-if="isPollingStats && !resultsDialog"
          type="info"
          dense
          class="mb-0"
        >
          Sending the selected emails. This continues automatically until the
          campaign finishes.
          <v-btn text small color="primary" @click="resultsDialog = true">
            View progress
          </v-btn>
          <v-btn
            v-if="canStopCampaign"
            text
            small
            color="error"
            :loading="stopping"
            @click="stopDialog = true"
          >
            Stop
          </v-btn>
        </v-alert>

        <!-- User count info -->
        <v-row v-if="!loading || users.length > 0">
          <v-col cols="12">
            <div class="text-subtitle-2 grey--text">
              Showing {{ users.length }} on this page
              <span v-if="hasMore">· more on the next page</span>
              | Selected: {{ selectedUsers.length }}
            </div>
            <div class="caption grey--text text--darken-1 mt-1">
              Each page is loaded from the server. Selections stay in place as
              you move between pages.
            </div>
            <div
              v-if="selectedUsers.length > 0"
              class="caption grey--text text--darken-1 mt-1"
            >
              <v-icon small color="info">mdi-information-outline</v-icon>
              {{ getAlreadySentCount() }} users in your selection already
              received an email today and will be skipped.
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Loading indicator -->
      <v-card-text
        v-if="loading && users.length === 0"
        class="text-center py-5"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="50"
        ></v-progress-circular>
        <div class="mt-4">Loading users...</div>
      </v-card-text>

      <v-data-table
        v-else
        :value="selectedUsers"
        @input="handleSelectionChange"
        :headers="headers"
        :items="displayUsers"
        :loading="loading && users.length > 0"
        item-key="uid"
        show-select
        :options.sync="tableOptions"
        :server-items-length="serverItemsLength"
        class="elevation-1"
        :items-per-page="tableOptions.itemsPerPage"
        :footer-props="{
          'items-per-page-options': [25, 50, 100, 200, 500, 1000],
          'items-per-page-text': 'Rows per page:'
        }"
        ref="dataTable"
        @update:page="onServerPageChange"
        @update:items-per-page="handleItemsPerPageChange"
      >
        <!-- Add top pagination that looks exactly like the bottom pagination -->
        <template v-slot:top>
          <div class="d-flex justify-end align-center px-4 py-2">
            <span class="mr-4 grey--text text--darken-1">Rows per page:</span>
            <v-select
              v-model="tableOptions.itemsPerPage"
              :items="[25, 50, 100, 200, 500, 1000]"
              dense
              outlined
              hide-details
              class="per-page-select mr-4"
              style="width: 120px"
              @change="handleItemsPerPageChange"
            ></v-select>

            <v-pagination
              v-model="tableOptions.page"
              :length="serverPageCount"
              :total-visible="7"
              :disabled="loading"
              show-first-last-page
              @input="onServerPageChange"
            ></v-pagination>
          </div>
        </template>

        <template v-slot:header-data-table-select>
          <v-simple-checkbox
            color="primary"
            :value="selectAll"
            :indeterminate="isIndeterminateValue"
            @input="toggleSelectAll"
          ></v-simple-checkbox>
        </template>
        <template #[`item.photoURL`]="{ item }">
          <v-avatar size="36">
            <img
              v-if="item.photoURL"
              :src="item.photoURL"
              :alt="item.displayName"
            />
            <v-icon v-else>mdi-account</v-icon>
          </v-avatar>
        </template>
        <template #[`item.emailPreferences`]="{ item }">
          <v-chip :color="getEmailPreferenceColor(item)" small>
            {{ getEmailPreferenceLabel(item) }}
          </v-chip>
        </template>
        <template #[`item.emailStatus`]="{ item }">
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-if="item.emailStatus?.status === 'sent'"
                  :color="hasEmailSentToday(item) ? 'success' : 'info'"
                  v-bind="attrs"
                  v-on="on"
                  small
                  class="mr-1"
                >
                  mdi-email-check
                </v-icon>
              </template>
              <span>{{ getEmailSentTooltip(item) }}</span>
            </v-tooltip>
            <span class="caption" v-if="item.emailStatus?.status === 'sent'">
              {{ getEmailDateDisplay(item) }}
            </span>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Email Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="800">
      <v-card>
        <v-card-title>
          Email Preview
          <v-spacer></v-spacer>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="!previewHTML" type="info" class="mb-4">
            Select an email template to preview it
          </v-alert>
          <div v-else>
            <v-card outlined>
              <v-card-title class="text-subtitle-1 pb-0">
                {{ selectedTemplate.name }}
              </v-card-title>
              <div class="text-subtitle-2 grey--text px-4 pb-3">
                {{ selectedTemplate.subject }}
              </div>
              <v-divider></v-divider>
              <v-card-text>
                <iframe
                  :srcdoc="previewHTML"
                  style="width: 100%; height: 500px; border: none;"
                ></iframe>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Date Filter Dialog -->
    <v-dialog v-model="dateFilterDialog" max-width="400">
      <v-card>
        <v-card-title>
          Select Date to Exclude Users
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Users who received emails on this date will be excluded from
            selection.
          </p>
          <v-date-picker
            v-model="selectedFilterDate"
            full-width
            class="mt-2"
          ></v-date-picker>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dateFilterDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="applyDateFilter">Apply Filter</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Date Range Filter Dialog -->
    <v-dialog v-model="dateRangeFilterDialog" max-width="500">
      <v-card>
        <v-card-title>
          Select Date Range to Exclude Users
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            Users who received emails between these dates will be excluded from
            selection.
          </p>
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRange.start"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateRange.start"
                  @input="startDateMenu = false"
                  :max="dateRange.end"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRange.end"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateRange.end"
                  @input="endDateMenu = false"
                  :min="dateRange.start"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dateRangeFilterDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="applyDateRangeFilter"
            >Apply Filter</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Results Dialog -->
    <v-dialog v-model="resultsDialog" max-width="800">
      <v-card>
        <v-card-title>
          Campaign Results
          <v-spacer></v-spacer>
          <v-btn icon @click="closeResultsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert
            :type="campaignResult.success ? 'success' : 'error'"
            class="mb-4"
          >
            {{ campaignResult.message }}
          </v-alert>

          <div v-if="campaignResult.stats" class="stats-container">
            <div class="d-flex align-center justify-space-around flex-wrap">
              <v-chip
                class="ma-2"
                :color="getStatusColor(campaignStatusLabel)"
                outlined
              >
                Status: {{ campaignStatusLabel }}
              </v-chip>
              <v-chip class="ma-2" color="primary" outlined>
                Total: {{ campaignResult.stats.total || 0 }}
              </v-chip>
              <v-chip class="ma-2" color="success" outlined>
                Accepted: {{ campaignResult.stats.sent || 0 }}
              </v-chip>
              <v-chip class="ma-2" color="error" outlined>
                Failed: {{ campaignResult.stats.failed || 0 }}
              </v-chip>
              <v-chip class="ma-2" color="warning" outlined>
                Skipped: {{ campaignResult.stats.skipped || 0 }}
              </v-chip>
              <v-chip
                v-if="campaignResult.stats.unknown"
                class="ma-2"
                color="warning"
                outlined
              >
                Unknown: {{ campaignResult.stats.unknown }}
              </v-chip>
            </div>

            <!-- Show polling indicator -->
            <div v-if="isPollingStats" class="text-center mt-4">
              <v-progress-circular
                indeterminate
                color="primary"
                size="20"
                class="mr-2"
              ></v-progress-circular>
              <span class="caption">Updating stats...</span>
            </div>

            <!-- Detailed Campaign Information -->
            <v-expansion-panels v-if="campaignResult.campaign" class="mt-4">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  Campaign Details
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Start Time</v-list-item-title>
                        <v-list-item-subtitle>{{
                          formatDate(campaignResult.campaign.startTime)
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="campaignResult.campaign.endTime">
                      <v-list-item-content>
                        <v-list-item-title>End Time</v-list-item-title>
                        <v-list-item-subtitle>{{
                          formatDate(campaignResult.campaign.endTime)
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="campaignResult.campaign.lastUpdated">
                      <v-list-item-content>
                        <v-list-item-title>Last Updated</v-list-item-title>
                        <v-list-item-subtitle>{{
                          formatDate(campaignResult.campaign.lastUpdated)
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Batch History -->
              <v-expansion-panel
                v-if="campaignResult.campaign.batchHistory?.length"
              >
                <v-expansion-panel-header>
                  Batch History
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-timeline dense>
                    <v-timeline-item
                      v-for="batch in campaignResult.campaign.batchHistory"
                      :key="batch.batchIndex"
                      :color="getBatchColor(batch.stats)"
                      small
                    >
                      <div class="d-flex justify-space-between">
                        <span>Batch {{ batch.batchIndex + 1 }}</span>
                        <span>{{ formatDate(batch.timestamp) }}</span>
                      </div>
                      <div class="caption">
                        Accepted: {{ batch.stats.sent }} | Failed:
                        {{ batch.stats.failed }} | Skipped:
                        {{ batch.stats.skipped }}
                        <span v-if="batch.stats.unknown">
                          | Unknown: {{ batch.stats.unknown }}
                        </span>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Error Log -->
              <v-expansion-panel v-if="campaignResult.campaign.errors?.length">
                <v-expansion-panel-header>Error Log</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list dense>
                    <v-list-item
                      v-for="(error, index) in campaignResult.campaign.errors"
                      :key="index"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="error--text">{{
                          error.message
                        }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ formatDate(error.timestamp) }}
                          <span v-if="error.email"
                            >| Email: {{ error.email }}</span
                          >
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="canStopCampaign"
            color="error"
            outlined
            :loading="stopping"
            @click="stopDialog = true"
          >
            Stop remaining emails
          </v-btn>
          <v-btn
            v-if="campaignResult.status === 'paused' && autoContinueFailed"
            color="warning"
            :loading="sending"
            @click="resumeCurrentCampaign()"
          >
            Resume Campaign
          </v-btn>
          <v-btn color="primary" text @click="closeResultsDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="stopDialog" max-width="480">
      <v-card>
        <v-card-title>Stop remaining emails?</v-card-title>
        <v-card-text>
          The batch already being sent can still go out. Nothing after that
          will be sent.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="stopDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="stopping" @click="stopCurrentCampaign">
            Stop sending
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import firebase from 'firebase/app'
import 'firebase/auth'
import { getBaseURL } from '@/helpers/emails'
import {
  FILTER_OPTIONS,
  getCampaignTypeForFilter
} from '../../../../functions/constants/emailCampaigns'

export default {
  name: 'EmailCampaigns',
  components: {
    PageHeader
  },
  data() {
    return {
      search: '',
      users: [],
      loading: true,
      error: null,
      userCount: 0,
      headers: [
        { text: 'Avatar', value: 'photoURL', sortable: false, width: '80px' },
        { text: 'Name', value: 'displayName', sortable: true },
        { text: 'Email', value: 'email', sortable: true },
        {
          text: 'Email Preferences',
          value: 'emailPreferences',
          sortable: false
        },
        { text: 'Last Updated', value: 'date_updated', sortable: true },
        { text: 'Email Status', value: 'emailStatus', sortable: false }
      ],
      selectedUsers: [],
      itemsPerPage: 50,
      emailTemplates: [
        {
          name: 'Welcome email',
          path: '/email-templates/html/welcome/welcome.html',
          subject: 'Welcome to LinkBook'
        },
        {
          name: 'New Starter Plan Launch - £1.99/month',
          path: '/email-templates/html/marketing/starter-launch.html',
          subject: 'Five workspaces for £1.99 a month'
        },
        {
          name: 'Marketing Features Template 1',
          path: '/email-templates/html/marketing/template-1.html',
          subject:
            "New Features at LinkBook.io - We've Added More Power to Your Bookmarks!"
        },
        {
          name: 'Investor Pitch - AI Features',
          path: '/email-templates/html/marketing/investor-template.html',
          subject: 'LinkBook: Revolutionising Bookmark Management with AI'
        },
        {
          name: 'Performance Upgrades - Faster, Smoother, Better',
          path: '/email-templates/html/marketing/metadata-improvement.html',
          subject:
            '⚡ Major Performance Upgrades - 10-60x Faster Link Previews, SSR & More!'
        },
        {
          name: 'New Dashboard Launch - Analytics & Insights',
          path: '/email-templates/html/marketing/dashboard-launch.html',
          subject:
            '📊 Introducing Your New Dashboard - Analytics, Insights & Quick Actions!'
        },
        {
          name: 'AI-Powered Category Organiser',
          path: '/email-templates/html/marketing/ai-category-organiser.html',
          subject:
            '🤖 Introducing AI-Powered Category Organiser - Let AI Organise Your Bookmarks!'
        },
        {
          name: 'Workspace Groups - Private & Public',
          path: '/email-templates/html/marketing/workspace-private-public.html',
          subject:
            '🚀 Workspace Groups Are Here! - Private for Teams, Public for the World'
        },
        {
          name: 'AI-Powered Bookmark Summaries',
          path: '/email-templates/html/marketing/ai-summary.html',
          subject:
            '📄 Introducing AI-Powered Bookmark Summaries - Understand Links at a Glance!'
        },
        {
          name: 'Bring Your Own Key — OpenRouter AI',
          path: '/email-templates/html/marketing/byok-openrouter.html',
          subject:
            'Your AI, your key — manage OpenRouter usage in LinkBook with no usage limits'
        },
        {
          name: 'Ask AI in Workspaces',
          path: '/email-templates/html/marketing/ask-ai-workspace.html',
          subject:
            'Ask your workspace — retrieve answers from the bookmarks you already saved'
        },
        {
          name: 'Workspace Role-Based Permissions',
          path: '/email-templates/html/marketing/workspace-roles.html',
          subject:
            'Workspace roles are here — decide who can add, edit or delete bookmarks'
        }
      ],
      selectedTemplate: null,
      previewHTML: '',
      previewDialog: false,
      sending: false,
      resultsDialog: false,
      campaignResult: {
        success: false,
        message: '',
        stats: null
      },
      filterOptions: Object.values(FILTER_OPTIONS),
      selectedFilter: null,
      activeFilter: null, // Tracks currently active filter
      currentCampaignId: null,
      statsPollingInterval: null,
      isPollingStats: false,
      statsPollingInFlight: false,
      statsPollingFailures: 0,
      autoContinueFailed: false,
      continuationInFlight: false,
      stopping: false,
      stopRequested: false,
      stopDialog: false,
      bulkEmailV2Enabled: process.env.VUE_APP_BULK_EMAIL_V2 === 'true',
      tableOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ['displayName'],
        sortDesc: [false]
      },
      pageTokens: { 1: null },
      hasMore: false,
      serverItemsLength: 0,
      loadedPage: 0,
      loadedPageSize: 50,
      activeQueryKey: '',
      fetchRequestId: 0,
      suppressPageFetch: false,
      usersReady: false,
      searchTimer: null,
      selectAll: false,
      isIndeterminateValue: false,
      showScrollTop: false,
      scrollThreshold: 300, // Show scroll button after scrolling 300px
      dateFilterDialog: false,
      dateRangeFilterDialog: false,
      startDateMenu: false,
      endDateMenu: false,
      selectedFilterDate: new Date().toISOString().substr(0, 10), // Today's date in YYYY-MM-DD format
      dateRange: {
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .substr(0, 10), // 7 days ago
        end: new Date().toISOString().substr(0, 10) // Today
      },
      externalEmails: '',
      externalName: '',
      showExternalEmailSection: false
    }
  },
  computed: {
    isAdminLoggedIn() {
      return this.$store.state.auth.permissions.admin
    },
    campaignType() {
      // Determine campaign type based on selected filter using shared constants
      return getCampaignTypeForFilter(this.selectedFilter)
    },
    allSelectedUsersAlreadySent() {
      if (!this.selectedUsers.length) return false
      return this.selectedUsers.every(user => this.hasEmailSentToday(user))
    },
    campaignStatusLabel() {
      const status = this.campaignResult.status
      if (
        status === 'paused' &&
        !this.autoContinueFailed &&
        !this.stopRequested
      ) {
        return 'continuing'
      }
      return status || 'processing'
    },
    canStopCampaign() {
      return (
        !!this.currentCampaignId &&
        ['processing', 'queued', 'paused', 'staging'].includes(
          this.campaignResult.status
        ) &&
        !this.stopRequested
      )
    },
    displayUsers() {
      const items = [...this.users]
      const sortBy =
        (this.tableOptions.sortBy && this.tableOptions.sortBy[0]) ||
        'displayName'
      const sortDesc = Boolean(
        this.tableOptions.sortDesc && this.tableOptions.sortDesc[0]
      )

      items.sort((a, b) => {
        const aValue = a[sortBy] == null ? '' : a[sortBy]
        const bValue = b[sortBy] == null ? '' : b[sortBy]
        if (aValue === bValue) return 0
        if (sortDesc) return aValue > bValue ? -1 : 1
        return aValue < bValue ? -1 : 1
      })

      return items
    },
    serverPageCount() {
      const size = this.normalisePageSize(this.tableOptions.itemsPerPage)
      if (!this.serverItemsLength) return 1
      return Math.max(1, Math.ceil(this.serverItemsLength / size))
    }
  },
  created() {
    // Wait for Firebase Auth to initialize
    this.waitForAuth().then(() => {
      this.checkAdminStatus().then(isAdmin => {
        if (isAdmin) {
          this.usersReady = true
          this.fetchUsers({ force: true })
        } else {
          this.loading = false
        }
      })
    })
  },
  mounted() {
    // Add scroll event listener
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    // Clean up any ongoing polling when the component is destroyed
    this.stopPollingStats()
    clearTimeout(this.searchTimer)

    // Remove scroll event listener
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      // Show button when page is scrolled down
      this.showScrollTop = window.scrollY > this.scrollThreshold
    },

    scrollToTop() {
      // Smooth scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },

    async waitForAuth() {
      return new Promise(resolve => {
        // If Firebase auth state already loaded in Vuex
        if (this.$store.state.auth.user && this.$store.state.auth.user.uid) {
          resolve()
          return
        }

        // Use Firebase's onAuthStateChanged to wait for auth
        const unsubscribe = firebase.auth().onAuthStateChanged(user => {
          if (user) {
            // Force token refresh to ensure we have the latest custom claims
            user
              .getIdToken(true)
              .then(() => {
                unsubscribe() // Stop listening
                resolve()
              })
              .catch(() => {
                resolve() // Continue anyway
              })
          } else {
            unsubscribe() // Stop listening
            resolve() // Continue anyway, checkAdminStatus will handle the error
          }
        })
      })
    },

    async checkAdminStatus() {
      try {
        // Try getting current user directly from Firebase first
        const currentUser = firebase.auth().currentUser

        // If not available, use the store as backup
        const storeUser = this.$store.state.auth.user

        if (!currentUser && !storeUser) {
          this.error = 'No user logged in. Please sign in again.'
          return false
        }

        // Use Firebase user if available, otherwise fall back to store
        const user = currentUser || storeUser

        // Get fresh token with claims
        try {
          const idTokenResult = await user.getIdTokenResult(true)
          const isAdmin = idTokenResult.claims.admin === true

          if (!isAdmin) {
            this.error = "You don't have admin permissions to view this page"
            return false
          }

          return true
        } catch (tokenError) {
          // Fall back to store permissions
          const isAdmin = this.$store.state.auth.permissions.admin === true

          if (!isAdmin) {
            this.error = "You don't have admin permissions to view this page"
            return false
          }

          return isAdmin
        }
      } catch (error) {
        this.error = `Admin check error: ${error.message}`
        return false
      }
    },
    async fetchUsers({ force = false, selectPage = false } = {}) {
      if (!this.usersReady) return

      const pageSize = this.normalisePageSize(this.tableOptions.itemsPerPage)
      const page = this.tableOptions.page || 1
      const pageToken = page > 1 ? this.pageTokens[page] || null : null
      if (page > 1 && !pageToken) {
        this.suppressPageFetch = true
        this.tableOptions.page = this.loadedPage || 1
        this.suppressPageFetch = false
        return
      }

      const filterWindow = this.currentFilterWindow()
      const queryKey = JSON.stringify({
        page,
        pageSize,
        pageToken,
        search: (this.search || '').trim(),
        filter: this.selectedFilter || null,
        startMs: filterWindow ? filterWindow.startMs : null,
        endMs: filterWindow ? filterWindow.endMs : null,
        endInclusive: filterWindow ? filterWindow.endInclusive : false
      })

      if (!force && queryKey === this.activeQueryKey) return

      const requestId = ++this.fetchRequestId
      this.activeQueryKey = queryKey
      this.loading = true
      this.error = null

      try {
        const currentUser = firebase.auth().currentUser
        if (!currentUser) {
          throw new Error('Unable to get current user. Please sign in again.')
        }

        const idToken = await currentUser.getIdToken(true)
        let result = null
        let requestToken = pageToken

        for (let hop = 0; hop < 5; hop += 1) {
          const response = await fetch(
            `${process.env.VUE_APP_API}getAllUsers`,
            {
              method: 'POST',
              mode: 'cors',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${idToken}`
              },
              body: JSON.stringify({
                adminUid: currentUser.uid,
                pageSize,
                pageToken: requestToken,
                search: (this.search || '').trim(),
                filter: this.selectedFilter,
                startMs: filterWindow ? filterWindow.startMs : null,
                endMs: filterWindow ? filterWindow.endMs : null,
                endInclusive: Boolean(filterWindow && filterWindow.endInclusive)
              })
            }
          )

          if (!response.ok) {
            const errorText = await response.text()
            throw new Error(
              `Failed to fetch users: ${response.status} ${response.statusText}. ${errorText}`
            )
          }

          result = await response.json()
          if (requestId !== this.fetchRequestId) return
          if (result.error || result.success === false) {
            throw new Error(
              result.error || result.message || 'Failed to fetch users'
            )
          }

          if (
            (result.users || []).length > 0 ||
            !result.hasMore ||
            !result.nextPageToken
          ) {
            break
          }

          requestToken = result.nextPageToken
        }

        const preservedSelection = selectPage
          ? null
          : this.selectedUsers.slice()
        this.users = (result.users || []).map(user => {
          return {
            ...user,
            date_updated:
              user.date_updated && user.date_updated !== 'N/A'
                ? this.formatDate(new Date(user.date_updated))
                : 'N/A',
            lastSignIn: user.lastSignInTime
              ? new Date(user.lastSignInTime)
              : null,
            displayName: user.displayName || 'Unknown User'
          }
        })
        this.userCount = this.users.length
        this.hasMore = Boolean(result.hasMore && result.nextPageToken)
        if (result.nextPageToken) {
          this.$set(this.pageTokens, page + 1, result.nextPageToken)
        }
        this.serverItemsLength = this.hasMore
          ? page * pageSize + 1
          : (page - 1) * pageSize + this.users.length
        this.loadedPage = page
        this.loadedPageSize = pageSize

        const selection = selectPage ? this.users.slice() : preservedSelection
        this.$nextTick(() => {
          if (requestId !== this.fetchRequestId) return
          this.selectedUsers = selection
          this.updateSelectionState()
        })
      } catch (error) {
        if (requestId !== this.fetchRequestId) return
        this.activeQueryKey = ''
        this.error = error.message
        this.$notify({
          group: 'action',
          type: 'error',
          title: 'Error',
          text: `Failed to load users: ${error.message}`
        })
      } finally {
        if (requestId === this.fetchRequestId) this.loading = false
      }
    },

    normalisePageSize(value) {
      const size = Number(value)
      if (!Number.isFinite(size) || size < 1) return 50
      return Math.min(1000, Math.floor(size))
    },

    currentFilterWindow() {
      if (
        process.env.NODE_ENV !== 'development' &&
        (this.selectedFilter === FILTER_OPTIONS.EXCLUDE_TODAYS_RECIPIENTS ||
          this.selectedFilter === FILTER_OPTIONS.SELECT_NOT_EMAILED_TODAY)
      ) {
        const start = new Date()
        start.setHours(0, 0, 0, 0)
        const end = new Date(start)
        end.setDate(end.getDate() + 1)
        return {
          startMs: start.getTime(),
          endMs: end.getTime(),
          endInclusive: false
        }
      }

      if (this.selectedFilter === FILTER_OPTIONS.EXCLUDE_BY_DATE) {
        if (!this.selectedFilterDate) return null
        const start = new Date(this.selectedFilterDate)
        start.setHours(0, 0, 0, 0)
        const end = new Date(start)
        end.setDate(end.getDate() + 1)
        return {
          startMs: start.getTime(),
          endMs: end.getTime(),
          endInclusive: false
        }
      }

      if (this.selectedFilter === FILTER_OPTIONS.EXCLUDE_BY_DATE_RANGE) {
        if (!this.dateRange.start || !this.dateRange.end) return null
        const start = new Date(this.dateRange.start)
        start.setHours(0, 0, 0, 0)
        const end = new Date(this.dateRange.end)
        end.setHours(23, 59, 59, 999)
        return {
          startMs: start.getTime(),
          endMs: end.getTime(),
          endInclusive: true
        }
      }

      return null
    },

    filterSelectsCurrentPage(filter) {
      return [
        FILTER_OPTIONS.ALL_USERS,
        FILTER_OPTIONS.MARKETING_OPT_IN,
        FILTER_OPTIONS.ALL_MARKETING_OPT_IN,
        FILTER_OPTIONS.FEATURES_ONLY,
        FILTER_OPTIONS.ALL_MARKETING_OPT_OUTS,
        FILTER_OPTIONS.SELECT_NOT_EMAILED_TODAY,
        FILTER_OPTIONS.RECENT_USERS_30_DAYS,
        FILTER_OPTIONS.INACTIVE_USERS_90_DAYS
      ].includes(filter)
    },

    async resetPaginationAndFetch(options = {}) {
      this.suppressPageFetch = true
      this.tableOptions.page = 1
      this.pageTokens = { 1: null }
      this.hasMore = false
      this.serverItemsLength = 0
      this.activeQueryKey = ''
      this.loadedPage = 0
      this.suppressPageFetch = false
      await this.fetchUsers({ force: true, ...options })
    },

    onServerPageChange(page) {
      if (this.suppressPageFetch || !this.usersReady) return
      const nextPage = Number(page) || this.tableOptions.page || 1
      if (nextPage === this.loadedPage) return
      this.fetchUsers()
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    getEmailPreferenceColor(user) {
      const prefs = user.emailPreferences
      if (prefs.marketingEmails && prefs.featureAnnouncements) {
        return 'success'
      } else if (!prefs.marketingEmails && !prefs.featureAnnouncements) {
        return 'error'
      } else {
        return 'warning'
      }
    },

    getEmailPreferenceLabel(user) {
      const prefs = user.emailPreferences
      if (prefs.marketingEmails && prefs.featureAnnouncements) {
        return 'All Emails'
      } else if (!prefs.marketingEmails && !prefs.featureAnnouncements) {
        return 'No Marketing'
      } else if (prefs.featureAnnouncements) {
        return 'Features Only'
      } else {
        return 'Marketing Only'
      }
    },

    async previewEmailTemplate() {
      if (!this.selectedTemplate) return

      try {
        const response = await fetch(this.selectedTemplate.path)
        let html = await response.text()

        // Replace placeholder tokens with sample values
        html = html
          .replace(/\{\{userEmail\}\}/g, 'user@example.com')
          .replace(/\{\{displayName\}\}/g, 'there')
          .replace(/\{\{unsubscribeToken\}\}/g, 'sample-token-123456')
          .replace(/https:\/\/linkbook\.io/g, getBaseURL())

        this.previewHTML = html
      } catch (error) {
        this.$notify({
          group: 'action',
          type: 'error',
          title: 'Error',
          text: 'Failed to load email template preview'
        })
      }
    },

    async applyFilter() {
      this.selectedUsers = []
      this.selectAll = false
      this.isIndeterminateValue = false
      this.activeFilter = null

      if (this.selectedFilter === FILTER_OPTIONS.EXCLUDE_BY_DATE) {
        this.dateFilterDialog = true
        return
      }
      if (this.selectedFilter === FILTER_OPTIONS.EXCLUDE_BY_DATE_RANGE) {
        this.dateRangeFilterDialog = true
        return
      }

      const selectPage = this.filterSelectsCurrentPage(this.selectedFilter)
      await this.resetPaginationAndFetch({ selectPage })

      if (selectPage && this.selectedFilter === FILTER_OPTIONS.ALL_USERS) {
        this.$notify({
          group: 'action',
          type: 'info',
          title: 'This page is selected',
          text: `Selected ${this.users.length} users on this page. Move to the next page and select again to add more.`
        })
      }
    },

    async sendMarketingEmails(emails) {
      const emailsToSend = emails.filter(user => !this.hasEmailSentToday(user))

      if (emailsToSend.length === 0) {
        return {
          success: false,
          message: 'All selected users have already received an email today',
          stats: {
            total: emails.length,
            sent: 0,
            failed: 0,
            skipped: emails.length
          }
        }
      }

      const result = await this.startEmailCampaign(emailsToSend)
      return {
        ...result,
        stats: result.stats || {
          total: emails.length,
          sent: 0,
          failed: 0,
          skipped: emails.length - emailsToSend.length,
          unknown: 0
        }
      }
    },

    createCampaignIdempotencyKey() {
      if (
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
      ) {
        return crypto.randomUUID()
      }

      return `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`
    },

    async startEmailCampaign(recipients) {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('Unable to get current user. Please sign in again.')
      }

      const idToken = await currentUser.getIdToken(false)
      const endpoint = this.bulkEmailV2Enabled
        ? 'sendBulkEmailsMailgunV2'
        : 'sendBulkEmailsMailgun'

      const response = await fetch(`${process.env.VUE_APP_API}${endpoint}`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          senderUid: currentUser.uid,
          emails: recipients.map(user => ({
            email: user.email,
            uid: user.uid,
            displayName: user.displayName,
            userData: user.userData
          })),
          templatePath: this.selectedTemplate.path,
          subject: this.selectedTemplate.subject,
          campaignType: this.campaignType,
          idempotencyKey: this.createCampaignIdempotencyKey()
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        return {
          success: false,
          message: `Failed to send campaign: ${response.status} ${errorText}`,
          stats: null
        }
      }

      const result = await response.json()

      if (!result.success) {
        return {
          success: false,
          message: result.message || 'Failed to send campaign',
          stats: null
        }
      }

      return {
        success: true,
        message: result.message || 'Campaign queued successfully',
        stats: result.stats,
        campaignId: result.campaignId,
        status: result.status || 'processing'
      }
    },

    hasEmailSentToday(user) {
      // Skip the check in development mode
      if (process.env.NODE_ENV === 'development') {
        return false
      }

      if (!user.emailStatus || user.emailStatus.status !== 'sent') {
        return false
      }

      // Check using both campaignDate and emailTimestamp to determine if email was sent today
      let emailDate = null

      // First try the emailTimestamp we added when processing campaigns
      if (user.emailStatus.emailTimestamp) {
        emailDate = new Date(user.emailStatus.emailTimestamp)
      }
      // Then try campaignDate
      else if (user.emailStatus.campaignDate) {
        emailDate = new Date(user.emailStatus.campaignDate)
      }
      // Last resort: fall back to the status timestamp
      else if (user.emailStatus.timestamp) {
        if (user.emailStatus.timestamp.seconds) {
          emailDate = new Date(user.emailStatus.timestamp.seconds * 1000)
        } else {
          emailDate = new Date(user.emailStatus.timestamp)
        }
      }

      // If we couldn't get a date, return false
      if (!emailDate) {
        return false
      }

      // Get today's date with time set to 00:00:00
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      // Get tomorrow's date with time set to 00:00:00
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      // Check if email was sent today (between today at 00:00:00 and tomorrow at 00:00:00)
      const isToday = emailDate >= today && emailDate < tomorrow

      return isToday
    },

    async sendCampaign() {
      if (!this.selectedTemplate || this.selectedUsers.length === 0) return

      this.sending = true
      this.autoContinueFailed = false
      this.stopRequested = false

      try {
        // Get only the actually selected users, not affected by search/pagination
        const selectedUserIds = new Set(
          this.selectedUsers.map(user => user.uid)
        )
        const usersToSend = this.users.filter(user =>
          selectedUserIds.has(user.uid)
        )

        // Send only the selected users
        const response = await this.sendMarketingEmails(usersToSend)

        this.campaignResult = {
          success: response.success,
          message: response.message,
          stats: {
            total: response.stats?.total || 0,
            sent: response.stats?.sent || 0,
            failed: response.stats?.failed || 0,
            skipped: response.stats?.skipped || 0,
            unknown: response.stats?.unknown || 0
          },
          status: response.status || 'processing',
          campaign: null
        }

        // If campaign was successful and we have a campaign ID, start polling for updates
        if (response.success && response.campaignId) {
          this.currentCampaignId = response.campaignId
          this.startPollingStats()
        }

        // Show results dialog
        this.resultsDialog = true
      } catch (error) {
        this.campaignResult = {
          success: false,
          message: `Failed to send campaign: ${error.message}`,
          stats: { total: 0, sent: 0, failed: 0, skipped: 0 },
          status: 'error'
        }
        this.resultsDialog = true
      } finally {
        this.sending = false
      }
    },

    // New methods for polling campaign stats
    startPollingStats() {
      if (this.statsPollingInterval) {
        clearInterval(this.statsPollingInterval)
      }

      this.isPollingStats = true
      this.statsPollingFailures = 0

      // Poll every 5 seconds
      this.statsPollingInterval = setInterval(() => {
        this.fetchCampaignStats()
      }, 5000)

      // Start immediately
      this.fetchCampaignStats()
    },

    stopPollingStats() {
      if (this.statsPollingInterval) {
        clearInterval(this.statsPollingInterval)
        this.statsPollingInterval = null
      }

      this.isPollingStats = false
      this.statsPollingInFlight = false
    },

    async fetchCampaignStats() {
      if (!this.currentCampaignId || this.statsPollingInFlight) return
      this.statsPollingInFlight = true

      try {
        const result = await this.getCampaignStats(this.currentCampaignId)

        if (result.success && result.campaign) {
          this.statsPollingFailures = 0
          // Update the campaign result with the latest stats
          this.campaignResult = {
            ...this.campaignResult,
            stats: {
              total: result.campaign.stats.total || 0,
              sent: result.campaign.stats.sent || 0,
              failed: result.campaign.stats.failed || 0,
              skipped: result.campaign.stats.skipped || 0,
              unknown: result.campaign.stats.unknown || 0
            },
            status: result.campaign.status,
            campaign: result.campaign
          }

          const status = result.campaign.status
          const shouldContinue =
            status === 'paused' &&
            !this.stopRequested &&
            !this.autoContinueFailed &&
            this.campaignHasRemaining(result.campaign)

          if (shouldContinue) {
            this.campaignResult = {
              ...this.campaignResult,
              message: 'Continuing with the remaining selected emails.'
            }
            this.continuePausedCampaign()
            return
          }

          // Stop once the campaign has finished or continuation needs a manual resume
          if (
            status === 'completed' ||
            status === 'completed_with_failures' ||
            status === 'cancelled' ||
            status === 'paused' ||
            status === 'error'
          ) {
            this.stopPollingStats()
          }
        }
      } catch (error) {
        this.statsPollingFailures++
        if (this.statsPollingFailures >= 3) {
          this.campaignResult = {
            ...this.campaignResult,
            message:
              'Campaign is still running, but progress updates are temporarily unavailable.'
          }
        }
      } finally {
        this.statsPollingInFlight = false
      }
    },

    async getCampaignStats(campaignId) {
      // Get current user directly from Firebase
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('Unable to get current user. Please sign in again.')
      }

      const idToken = await currentUser.getIdToken(false)

      // Call the getCampaignStats API endpoint
      const response = await fetch(
        `${process.env.VUE_APP_API}getCampaignStats`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            campaignId: campaignId
          })
        }
      )

      if (!response.ok) {
        throw new Error(
          `Failed to fetch campaign stats: ${response.status} ${response.statusText}`
        )
      }

      return await response.json()
    },

    campaignHasRemaining(campaign) {
      const total = Number(campaign.totalEmails || campaign.stats?.total || 0)
      if (!total) return false

      if (campaign.processedEmails !== undefined) {
        return Number(campaign.processedEmails) < total
      }

      const stats = campaign.stats || {}
      const processed =
        (stats.sent || 0) +
        (stats.failed || 0) +
        (stats.skipped || 0) +
        (stats.unknown || 0)
      return processed < total
    },

    async continuePausedCampaign() {
      if (
        this.continuationInFlight ||
        this.sending ||
        this.autoContinueFailed ||
        this.stopRequested
      ) {
        return
      }

      this.continuationInFlight = true
      try {
        const resumed = await this.resumeCurrentCampaign({ automatic: true })
        if (!resumed) {
          this.autoContinueFailed = true
          this.campaignResult = {
            ...this.campaignResult,
            status: 'paused',
            message:
              'The campaign paused before every selected email was sent. Resume it to continue.'
          }
          this.stopPollingStats()
        }
      } finally {
        this.continuationInFlight = false
      }
    },

    async resumeCurrentCampaign({ automatic = false } = {}) {
      if (!this.currentCampaignId || this.sending) return false

      this.sending = true
      try {
        const currentUser = firebase.auth().currentUser
        if (!currentUser) {
          throw new Error('Unable to get current user. Please sign in again.')
        }

        const idToken = await currentUser.getIdToken(false)
        const endpoint = this.bulkEmailV2Enabled
          ? 'resumeEmailCampaignV2'
          : 'resumeEmailCampaign'
        const response = await fetch(`${process.env.VUE_APP_API}${endpoint}`, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            campaignId: this.currentCampaignId,
            senderUid: currentUser.uid
          })
        })

        const result = await response.json()
        if (this.stopRequested) {
          return false
        }
        if (!response.ok || !result.success) {
          const alreadyContinuing =
            automatic && /not paused/i.test(result.message || '')
          if (!alreadyContinuing) {
            throw new Error(result.message || 'Unable to resume campaign')
          }
        } else {
          this.autoContinueFailed = false
          this.campaignResult = {
            ...this.campaignResult,
            success: true,
            message: automatic
              ? 'Continuing with the remaining selected emails.'
              : result.message,
            status: result.status || 'processing'
          }
        }
        this.startPollingStats()
        return true
      } catch (error) {
        this.campaignResult = {
          ...this.campaignResult,
          success: false,
          status: 'paused',
          message: `Failed to resume campaign: ${error.message}`
        }
        return false
      } finally {
        this.sending = false
      }
    },

    closeResultsDialog() {
      this.resultsDialog = false
    },

    async stopCurrentCampaign() {
      if (!this.currentCampaignId || this.stopping) return

      this.stopRequested = true
      this.stopping = true
      try {
        const currentUser = firebase.auth().currentUser
        if (!currentUser) {
          throw new Error('Unable to get current user. Please sign in again.')
        }

        const idToken = await currentUser.getIdToken(false)
        const response = await fetch(
          `${process.env.VUE_APP_API}stopEmailCampaign`,
          {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`
            },
            body: JSON.stringify({
              campaignId: this.currentCampaignId
            })
          }
        )
        const result = await response.json()
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Unable to stop campaign')
        }

        this.campaignResult = {
          ...this.campaignResult,
          success: true,
          message:
            result.message ||
            'Remaining emails have been stopped. Any batch already in progress may still be sent.',
          status: result.status || 'cancelled'
        }
        this.stopDialog = false
        this.resultsDialog = true
        this.stopPollingStats()
      } catch (error) {
        this.stopRequested = false
        this.campaignResult = {
          ...this.campaignResult,
          success: false,
          message: `Failed to stop campaign: ${error.message}`
        }
      } finally {
        this.stopping = false
      }
    },

    getStatusColor(status) {
      switch (status) {
        case 'completed':
          return 'success'
        case 'completed_with_failures':
          return 'warning'
        case 'error':
          return 'error'
        case 'queued':
        case 'processing':
          return 'primary'
        case 'paused':
        case 'continuing':
          return 'warning'
        case 'cancelled':
          return 'grey'
        default:
          return 'grey'
      }
    },

    handleItemsPerPageChange(newItemsPerPage) {
      const pageSize = this.normalisePageSize(newItemsPerPage)
      this.itemsPerPage = pageSize
      if (
        pageSize === this.loadedPageSize &&
        (this.tableOptions.page || 1) === this.loadedPage
      ) {
        this.tableOptions.itemsPerPage = pageSize
        return
      }

      this.suppressPageFetch = true
      this.tableOptions.itemsPerPage = pageSize
      this.tableOptions.page = 1
      this.suppressPageFetch = false
      this.pageTokens = { 1: null }
      this.activeQueryKey = ''
      this.fetchUsers({ force: true })
    },

    getBatchColor(stats) {
      if (stats.failed > 0) return 'error'
      if (stats.unknown > 0) return 'warning'
      if (stats.skipped > 0) return 'warning'
      return 'success'
    },

    getAlreadySentCount() {
      return this.selectedUsers.filter(user => this.hasEmailSentToday(user))
        .length
    },

    getEmailSentTooltip(user) {
      if (user.emailStatus?.status === 'sent') {
        // Get the most relevant timestamp for the email
        let emailDate = null

        // First try the emailTimestamp we added when processing campaigns
        if (user.emailStatus.emailTimestamp) {
          emailDate = new Date(user.emailStatus.emailTimestamp)
        }
        // Then try campaignDate
        else if (user.emailStatus.campaignDate) {
          emailDate = new Date(user.emailStatus.campaignDate)
        }
        // Last resort: fall back to the status timestamp
        else if (user.emailStatus.timestamp) {
          if (user.emailStatus.timestamp.seconds) {
            emailDate = new Date(user.emailStatus.timestamp.seconds * 1000)
          } else {
            emailDate = new Date(user.emailStatus.timestamp)
          }
        }

        if (!emailDate) {
          return 'Email sent (date unknown)'
        }

        // Format the date
        const formattedDate = emailDate.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })

        const formattedTime = emailDate.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit'
        })

        const isToday = this.hasEmailSentToday(user)

        // Also show the campaign ID for debugging
        const campaignInfo = user.emailStatus.campaignId
          ? ` (Campaign: ${user.emailStatus.campaignId.substring(0, 6)})`
          : ''

        return isToday
          ? `Email sent today at ${formattedTime} (${formattedDate})${campaignInfo}`
          : `Email sent on ${formattedDate} at ${formattedTime}${campaignInfo}`
      } else {
        return 'No email sent'
      }
    },

    getEmailDateDisplay(user) {
      if (user.emailStatus?.status === 'sent') {
        // Get the most relevant timestamp for the email
        let emailDate = null

        // First try the emailTimestamp we added when processing campaigns
        if (user.emailStatus.emailTimestamp) {
          emailDate = new Date(user.emailStatus.emailTimestamp)
        }
        // Then try campaignDate
        else if (user.emailStatus.campaignDate) {
          emailDate = new Date(user.emailStatus.campaignDate)
        }
        // Last resort: fall back to the status timestamp
        else if (user.emailStatus.timestamp) {
          if (user.emailStatus.timestamp.seconds) {
            emailDate = new Date(user.emailStatus.timestamp.seconds * 1000)
          } else {
            emailDate = new Date(user.emailStatus.timestamp)
          }
        }

        if (!emailDate) {
          return 'Date unknown'
        }

        // Get today's date with time set to 00:00:00
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // Get yesterday's date
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        // Format based on how recent the date is
        if (emailDate >= today) {
          return 'Today'
        } else if (emailDate >= yesterday) {
          return 'Yesterday'
        } else {
          // Format the date
          return emailDate.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        }
      } else {
        return 'No email sent'
      }
    },

    handleSelectionChange(selected) {
      const currentPageItems = this.displayUsers
      const currentPageIds = new Set(currentPageItems.map(item => item.uid))
      const previouslySelected = this.selectedUsers.filter(
        item => !currentPageIds.has(item.uid)
      )
      const newlySelected = selected.filter(item =>
        currentPageIds.has(item.uid)
      )

      this.selectedUsers = [...previouslySelected, ...newlySelected]
      setTimeout(() => {
        this.updateSelectionState()
      }, 0)
    },

    toggleSelectAll(value) {
      const currentPageItems = this.displayUsers
      const currentPageIds = new Set(currentPageItems.map(item => item.uid))

      if (value) {
        const previouslySelected = this.selectedUsers.filter(
          item => !currentPageIds.has(item.uid)
        )
        this.selectedUsers = [...previouslySelected, ...currentPageItems]
      } else {
        this.selectedUsers = this.selectedUsers.filter(
          item => !currentPageIds.has(item.uid)
        )
      }

      setTimeout(() => {
        this.updateSelectionState()
      }, 0)
    },

    updateSelectionState() {
      const currentPageItems = this.displayUsers

      if (currentPageItems.length === 0) {
        this.selectAll = false
        this.isIndeterminateValue = false
        return
      }

      const currentPageIds = new Set(currentPageItems.map(item => item.uid))
      const selectedIds = new Set(this.selectedUsers.map(item => item.uid))
      let selectedCount = 0
      currentPageIds.forEach(id => {
        if (selectedIds.has(id)) selectedCount += 1
      })

      this.selectAll =
        selectedCount === currentPageItems.length && selectedCount > 0
      this.isIndeterminateValue =
        selectedCount > 0 && selectedCount < currentPageItems.length
    },

    async applyDateFilter() {
      if (!this.selectedFilterDate) {
        this.$notify({
          group: 'action',
          type: 'error',
          title: 'Error',
          text: 'Please select a valid date'
        })
        return
      }

      this.dateFilterDialog = false
      await this.resetPaginationAndFetch({ selectPage: true })
      this.$notify({
        group: 'action',
        type: 'success',
        title: 'Filter Applied',
        text: this.hasMore
          ? `Loaded ${this.users.length} users not emailed on that date. More are on the next page.`
          : `Loaded ${this.users.length} users not emailed on that date.`
      })
    },

    async applyDateRangeFilter() {
      if (!this.dateRange.start || !this.dateRange.end) {
        this.$notify({
          group: 'action',
          type: 'error',
          title: 'Error',
          text: 'Please select valid start and end dates'
        })
        return
      }

      this.dateRangeFilterDialog = false
      await this.resetPaginationAndFetch({ selectPage: true })
      this.$notify({
        group: 'action',
        type: 'success',
        title: 'Date Range Filter Applied',
        text: this.hasMore
          ? `Loaded ${this.users.length} users not emailed in that range. More are on the next page.`
          : `Loaded ${this.users.length} users not emailed in that range.`
      })
    },

    // Add new method for sending to external emails
    async sendToExternalEmails() {
      if (!this.selectedTemplate || !this.externalEmails) return

      this.sending = true
      this.autoContinueFailed = false
      this.stopRequested = false

      try {
        // Parse the comma-separated emails
        const emailList = this.externalEmails
          .split(',')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'))

        if (emailList.length === 0) {
          throw new Error('No valid email addresses found')
        }

        const recipients = emailList.map(email => {
          const firstName = email.split('@')[0].split(/[._-]/)[0]
          const displayName =
            firstName.charAt(0).toUpperCase() + firstName.slice(1)
          return {
            email,
            uid: null,
            displayName,
            userData: { displayName }
          }
        })
        const result = await this.startEmailCampaign(recipients)

        if (!result.success) {
          throw new Error(result.message || 'Failed to send campaign')
        }

        this.campaignResult = {
          success: true,
          message: result.message || 'Campaign sent successfully',
          stats: result.stats || {
            total: emailList.length,
            sent: 0,
            failed: 0,
            skipped: 0,
            unknown: 0
          },
          campaignId: result.campaignId,
          status: result.status || 'processing',
          campaign: null
        }

        // If campaign was successful and we have a campaign ID, start polling for updates
        if (result.campaignId) {
          this.currentCampaignId = result.campaignId
          this.startPollingStats()
        }

        // Show results dialog
        this.resultsDialog = true

        // Clear the external emails field after successful send
        this.externalEmails = ''
      } catch (error) {
        this.campaignResult = {
          success: false,
          message: `Failed to send campaign: ${error.message}`,
          stats: { total: 0, sent: 0, failed: 0, skipped: 0 },
          status: 'error'
        }
        this.resultsDialog = true
      } finally {
        this.sending = false
      }
    }
  },
  watch: {
    selectedUsers: {
      handler() {
        this.updateSelectionState()
      },
      deep: true
    },
    tableOptions: {
      handler() {
        this.updateSelectionState()
      },
      deep: true
    },
    search() {
      if (!this.usersReady) return
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.resetPaginationAndFetch()
      }, 400)
    }
  }
}
</script>

<style scoped>
.email-campaigns {
  padding-bottom: 60px;
}

.stats-container {
  border-radius: 4px;
  padding: 8px;
}

/* Add scroll-to-top button styling */
.v-btn.v-btn--fab.v-btn--fixed {
  z-index: 100;
  transition: opacity 0.3s ease;
  opacity: 0.8;
}

.v-btn.v-btn--fab.v-btn--fixed:hover {
  opacity: 1;
  transform: translateY(-5px);
}

/* Add responsive styling for mobile */
@media (max-width: 600px) {
  .stats-container .v-chip {
    margin: 4px !important;
  }

  .v-btn.v-btn--fab.v-btn--fixed {
    bottom: 16px !important;
    right: 16px !important;
  }
}
</style>
